import React, { useEffect, useState } from "react";
import { BellRinging, X, Sparkle } from "@phosphor-icons/react";
import { ensurePushSubscription, isPushSupported, pushPermission } from "@/lib/webPush";
import { toast } from "sonner";

const SNOOZE_KEY = "micm_push_banner_snoozed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * v1.25.54 — One-time friendly banner urging the customer to allow browser
 * notifications so they get an instant "Thank You!" alert on their phone
 * the moment their payment is verified — even if the MICM app is closed.
 *
 * Never shown if:
 *   - The browser doesn't support push
 *   - Permission is already granted or explicitly denied
 *   - The user snoozed the banner in the last 7 days
 */
export default function PushEnableBanner() {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isPushSupported()) return;
        if (pushPermission() !== "default") return; // granted or denied → hide
        try {
            const snoozedAt = Number(localStorage.getItem(SNOOZE_KEY) || 0);
            if (snoozedAt && Date.now() - snoozedAt < SNOOZE_MS) return;
        } catch { /* noop */ }
        // Delay 2 s so it doesn't fight the initial dashboard render for attention.
        const t = setTimeout(() => setVisible(true), 2000);
        return () => clearTimeout(t);
    }, []);

    if (!visible) return null;

    const snooze = () => {
        try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch { /* noop */ }
        setVisible(false);
    };

    const enable = async () => {
        setBusy(true);
        const result = await ensurePushSubscription({ silent: false });
        setBusy(false);
        if (result?.ok) {
            toast.success("Notifications enabled! You'll get a Thank-You alert the instant your payment is verified.");
            setVisible(false);
        } else {
            // Clear diagnostic instead of a generic failure — helps the user + support debug.
            const reasonMap = {
                "denied": "Blocked in browser settings. Enable notifications for this site to receive Thank-You alerts.",
                "browser-unsupported": "This browser doesn't support push notifications. Try Chrome/Edge on Android or Safari 16.4+ on iOS.",
                "server-not-configured": "Server-side push setup is incomplete. Please tell your MICM admin to check /api/health/push.",
                "no-push-manager": "Your browser has push disabled at the OS level. Check your phone's app-notification settings.",
                "permission-default": "Please tap 'Allow' when the browser prompts you.",
                "subscribe-failed": `Subscribe failed on this device. Please try again in a moment. (${result?.error || "unknown"})`,
                "default": "Notifications dismissed. You'll see the Thank-You animation when you open the app instead.",
            };
            const msg = reasonMap[result?.reason] || reasonMap["default"];
            if (result?.reason === "denied") toast.error(msg);
            else toast.info(msg);
            snooze();
        }
    };

    return (
        <div
            data-testid="push-enable-banner"
            className="relative mx-auto mt-4 flex max-w-6xl items-start gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-4 py-3 shadow-sm"
        >
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 shadow-inner">
                <BellRinging size={18} weight="fill" className="text-white animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                    <Sparkle size={14} weight="fill" className="text-amber-500" />
                    <span>Get an instant Thank-You on your phone</span>
                </div>
                <p className="mt-0.5 text-xs text-slate-600">
                    Enable notifications and we'll ping you the moment your payment is verified — even when MICM is closed.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        data-testid="push-enable-btn"
                        disabled={busy}
                        onClick={enable}
                        className="rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-700 disabled:opacity-60"
                    >
                        {busy ? "Enabling…" : "Turn on notifications"}
                    </button>
                    <button
                        type="button"
                        data-testid="push-enable-snooze"
                        onClick={snooze}
                        className="rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
            <button
                type="button"
                aria-label="Dismiss"
                onClick={snooze}
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
                <X size={12} weight="bold" />
            </button>
        </div>
    );
}
